import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {FC, useState} from "react";
import {arrayMove} from "@dnd-kit/sortable";
import ColumnContainer from "./Board/Column";
import ReactDOM from "react-dom";
import ProposalCard from "./Board/ProposalCard";
import useCandidates from "hooks/useCandidates";
import {ICandidate} from "api/types";
import FullLoader from "components/loaders/FullLoader";

const COLUMNS = [
  {id: "contact", label: "Contact List"},
  {id: "progress", label: "In Progress"},
  {id: "done", label: "Active Clients"},
] as const;

const CandidatesPage: FC = () => {
  const {candidates, setCandidates, setChanges, patchUpdates, patching} = useCandidates();

  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 10}}));

  const [activeTask, setActiveTask] = useState<ICandidate | null>(null);

  const loading = candidates === undefined;

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-brand-cream">
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver}>
        <div className="flex-1 flex overflow-hidden">
          {loading ? (
            <FullLoader />
          ) : (
            COLUMNS.map((col, i) => {
              const colCandidates = candidates!.filter((e) => e.state === col.id);
              return (
                <ColumnContainer
                  key={col.id}
                  column={{id: col.id}}
                  deleteTask={deleteProposal}
                  changeProposalNote={changeProposalNote}
                  proposals={colCandidates}
                  isLast={i === COLUMNS.length - 1}
                >
                  <div className="flex items-center px-5 py-4 gap-3 border-b border-brand-border bg-brand-surface flex-shrink-0">
                    <span className="text-sm font-semibold text-brand-navy grow">{col.label}</span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-brand-cream text-brand-muted">
                      {colCandidates.length}
                    </span>
                  </div>
                </ColumnContainer>
              );
            })
          )}
        </div>
        {ReactDOM.createPortal(
          <DragOverlay className="z-10">
            {activeTask && (
              <ProposalCard changeProposalNote={changeProposalNote} proposal={activeTask} deleteTask={deleteProposal} />
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </div>
  );

  function deleteProposal(id: string) {
    setCandidates((o) => o?.filter((c) => c.id !== id));
    patchUpdates(false);
  }

  function changeProposalNote(id: string, note: string) {
    setChanges((o) => ({...o, [id]: {...o[id], note}}));
    setCandidates((o) => o?.map((c) => (c.id === id ? {...c, note} : c)));
    patchUpdates(false);
  }

  function onDragStart(event: DragStartEvent) {
    if (patching) return;
    if (event.active.data.current?.type === "Proposal") {
      setActiveTask(event.active.data.current.proposal);
    }
  }

  function onDragEnd(event: DragEndEvent) {
    patchUpdates();
    setActiveTask(null);

    const {active, over} = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveAColumn = active.data.current?.type === "Column";
    if (!isActiveAColumn) return;
  }

  function onDragOver(event: DragOverEvent) {
    const {active, over} = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveATask = active.data.current?.type === "Proposal";
    const isOverATask = over.data.current?.type === "Proposal";

    if (!isActiveATask) return;

    if (isActiveATask && isOverATask) {
      setCandidates((old) => {
        if (!old) return;
        const activeIndex = old.findIndex((t) => t.id === activeId);
        const overIndex = old.findIndex((t) => t.id === overId);

        if (old[activeIndex].state !== old[overIndex].state) {
          old[activeIndex].state = old[overIndex].state;
          setChanges((o) => ({...o, [activeId]: {...o[activeId], state: old[overIndex].state}}));
          return arrayMove(old, activeIndex, overIndex - 1);
        }

        return arrayMove(old, activeIndex, overIndex);
      });
    }

    const isOverAColumn = over.data.current?.type === "Column";

    if (isActiveATask && isOverAColumn) {
      setCandidates((old) => {
        if (!old) return;
        const activeIndex = old.findIndex((t) => t.id === activeId);
        old[activeIndex].state = overId as any;
        setChanges((o) => ({...o, [activeId]: {...o[activeId], state: overId as any}}));
        return arrayMove(old, activeIndex, activeIndex);
      });
    }
  }
};

export default CandidatesPage;
